/**
 * useReportFilters Hook
 * Reusable hook for managing URL-synced filter state in reports and lists
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  ReportFilters,
  ReportFilterConfig,
  ActiveFilter,
  FilterKey,
  FILTER_LABELS,
} from './types'

interface UseReportFiltersReturn {
  filters: ReportFilters
  updateFilter: (key: keyof ReportFilters, value: string) => void
  clearFilters: () => void
  hasActiveFilters: boolean
  getActiveFilterLabels: () => ActiveFilter[]
  getQueryParams: () => Record<string, string>
  isInitialized: boolean
}

const FILTER_URL_KEYS: Record<keyof ReportFilters, string> = {
  search: 'search',
  textSearch: 'textSearch',
  fpStatus: 'fpStatus',
  market: 'market',
  region: 'region',
  romFbc: 'romFbc',
  systemStatus: 'systemStatus',
  adoptionStatus: 'adoptionStatus',
  icuStatus: 'icuStatus',
  sellingStatus: 'sellingStatus',
  startDate: 'startDate',
  endDate: 'endDate',
  system: 'system',
  syncStatus: 'syncStatus',
  initiatedBy: 'initiatedBy',
  connectionStatus: 'connectionStatus',
  exceptionTypes: 'exceptionTypes',
  minGapDays: 'minGapDays',
  minVarianceAmount: 'minVarianceAmount',
  dataDomain: 'dataDomain',
  exceptionType: 'exceptionType',
  businessArea: 'businessArea',
  visibleToRole: 'visibleToRole',
}

export function useReportFilters(config: ReportFilterConfig): UseReportFiltersReturn {
  const { enabledFilters, defaults = {}, syncToUrl = true } = config
  const [searchParams, setSearchParams] = useSearchParams()
  const [isInitialized, setIsInitialized] = useState(false)

  // Build initial filters from URL params or defaults
  const getInitialFilters = useCallback((): ReportFilters => {
    const initial: ReportFilters = {}

    // Apply defaults first
    Object.entries(defaults).forEach(([key, value]) => {
      const isDateField = key === 'startDate' || key === 'endDate'
      if (enabledFilters.includes(key as FilterKey) || (isDateField && enabledFilters.includes('dateRange' as FilterKey))) {
        initial[key as keyof ReportFilters] = value
      }
    })

    // Override with URL params if syncToUrl is enabled
    if (syncToUrl) {
      const keysToSync = [...enabledFilters]
      if (enabledFilters.includes('dateRange' as FilterKey)) {
        keysToSync.push('startDate' as FilterKey, 'endDate' as FilterKey)
      }
      keysToSync.forEach((filterKey) => {
        const urlKey = FILTER_URL_KEYS[filterKey as keyof ReportFilters]
        if (urlKey) {
          const urlValue = searchParams.get(urlKey)
          if (urlValue !== null) {
            initial[filterKey as keyof ReportFilters] = urlValue
          }
        }
      })
    }

    return initial
  }, [enabledFilters, defaults, searchParams, syncToUrl])

  const [filters, setFilters] = useState<ReportFilters>(getInitialFilters)

  // Sync filters to URL when they change
  useEffect(() => {
    if (!syncToUrl) return
    if (!isInitialized) {
      setIsInitialized(true)
      return
    }

    // Use window.location.search instead of searchParams to avoid circular dependency
    // searchParams in dependency array causes infinite loop:
    // filters change -> setSearchParams -> URL updates -> searchParams object changes -> effect re-runs
    const params = new URLSearchParams(window.location.search)

    // Update URL params based on current filters
    Object.entries(FILTER_URL_KEYS).forEach(([filterKey, urlKey]) => {
      const value = filters[filterKey as keyof ReportFilters]
      const defaultValue = defaults[filterKey as keyof ReportFilters]

      if (value && value !== defaultValue && value !== '') {
        params.set(urlKey, value)
      } else {
        params.delete(urlKey)
      }
    })

    setSearchParams(params, { replace: true })
  }, [filters, isInitialized, setSearchParams, syncToUrl, defaults])

  const updateFilter = useCallback((key: keyof ReportFilters, value: string) => {
    setFilters((prev) => {
      const updated = { ...prev, [key]: value }

      // Clear dependent filters
      if (key === 'market') {
        // Region depends on market
        updated.region = ''
      }

      return updated
    })
  }, [])

  const clearFilters = useCallback(() => {
    const cleared: ReportFilters = {}
    // Apply defaults when clearing
    Object.entries(defaults).forEach(([key, value]) => {
      if (enabledFilters.includes(key as FilterKey)) {
        cleared[key as keyof ReportFilters] = value
      }
    })
    setFilters(cleared)
  }, [defaults, enabledFilters])

  const hasActiveFilters = useMemo(() => {
    return enabledFilters.some((filterKey) => {
      const value = filters[filterKey as keyof ReportFilters]
      const defaultValue = defaults[filterKey as keyof ReportFilters]
      return value && value !== '' && value !== defaultValue
    })
  }, [filters, enabledFilters, defaults])

  const getActiveFilterLabels = useCallback((): ActiveFilter[] => {
    const labels: ActiveFilter[] = []

    enabledFilters.forEach((filterKey) => {
      const value = filters[filterKey as keyof ReportFilters]
      const defaultValue = defaults[filterKey as keyof ReportFilters]

      if (value && value !== '' && value !== defaultValue) {
        // Handle date range specially
        if (filterKey === 'dateRange') {
          if (filters.startDate || filters.endDate) {
            const dateLabel = [filters.startDate, filters.endDate].filter(Boolean).join(' - ')
            labels.push({
              key: filterKey,
              label: FILTER_LABELS[filterKey],
              value: dateLabel,
            })
          }
        } else {
          labels.push({
            key: filterKey,
            label: FILTER_LABELS[filterKey],
            value,
          })
        }
      }
    })

    return labels
  }, [filters, enabledFilters, defaults])

  const getQueryParams = useCallback((): Record<string, string> => {
    const params: Record<string, string> = {}

    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== '') {
        params[key] = value
      }
    })

    return params
  }, [filters])

  return {
    filters,
    updateFilter,
    clearFilters,
    hasActiveFilters,
    getActiveFilterLabels,
    getQueryParams,
    isInitialized,
  }
}
